import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Users, Mail, ClipboardList, Home as HomeIcon } from "lucide-react";

export default function AdminDashboard() {
  const { user, loading, logout, authAxios } = useAuth();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("contacts");
  const [dataLoading, setDataLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    const client = authAxios();
    Promise.all([
      client.get("/admin/stats").then((r) => r.data),
      client.get("/admin/contacts").then((r) => r.data),
      client.get("/admin/applications").then((r) => r.data),
    ])
      .then(([s, c, a]) => { setStats(s); setContacts(c); setApps(a); })
      .catch(() => {})
      .finally(() => setDataLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const onLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-[#0A1128] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 border border-white flex items-center justify-center font-serif">H</div>
            <div>
              <div className="font-serif text-xl">Happy Home · Admin</div>
              <div className="text-xs text-[#A1A9BC]">Signed in as {user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-[#A1A9BC] hover:text-white flex items-center gap-2" data-testid="admin-view-site"><HomeIcon className="w-4 h-4"/>View site</Link>
            <button onClick={onLogout} data-testid="admin-logout" className="text-sm flex items-center gap-2 border border-white/20 px-4 py-2 hover:bg-white hover:text-[#0A1128] transition">
              <LogOut className="w-4 h-4"/> Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6 mb-12">
          <StatCard icon={Mail} label="Contact Enquiries" value={stats?.contacts ?? "—"} testId="stat-contacts"/>
          <StatCard icon={ClipboardList} label="Applications" value={stats?.applications ?? "—"} testId="stat-applications"/>
          <StatCard icon={Users} label="Helpers on File" value={stats?.helpers ?? "—"} testId="stat-helpers"/>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8 flex gap-8">
          {[
            { k: "contacts", label: `Contact Enquiries (${contacts.length})` },
            { k: "applications", label: `Applications (${apps.length})` },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`pb-3 text-sm border-b-2 -mb-px transition ${tab === t.k ? "border-[#688A4E] text-[#0A1128] font-semibold" : "border-transparent text-gray-500 hover:text-[#0A1128]"}`}
            >{t.label}</button>
          ))}
        </div>

        {dataLoading ? (
          <div className="text-gray-500">Loading…</div>
        ) : tab === "contacts" ? (
          <ContactsTable rows={contacts}/>
        ) : (
          <ApplicationsTable rows={apps}/>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div className="bg-white border border-gray-200 p-8 flex items-center justify-between" data-testid={testId}>
      <div>
        <div className="overline mb-2">{label}</div>
        <div className="font-serif text-5xl text-[#0A1128]">{value}</div>
      </div>
      <div className="w-14 h-14 border border-[#688A4E] flex items-center justify-center">
        <Icon className="w-6 h-6 text-[#688A4E]"/>
      </div>
    </div>
  );
}

function ContactsTable({ rows }) {
  if (rows.length === 0) return <div className="bg-white border border-gray-200 p-12 text-center text-gray-500">No contact enquiries yet.</div>;
  return (
    <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="admin-contacts-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F9F7]">
          <tr className="text-left text-xs tracking-widest uppercase text-gray-500">
            <th className="px-6 py-4">Name</th><th className="px-6 py-4">Email</th><th className="px-6 py-4">Phone</th><th className="px-6 py-4">Subject</th><th className="px-6 py-4">Message</th><th className="px-6 py-4">Received</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gray-100 hover:bg-[#F9F9F7]">
              <td className="px-6 py-4 font-medium text-[#0A1128]">{r.name}</td>
              <td className="px-6 py-4"><a href={`mailto:${r.email}`} className="text-[#688A4E] hover:underline">{r.email}</a></td>
              <td className="px-6 py-4">{r.phone || "—"}</td>
              <td className="px-6 py-4">{r.subject}</td>
              <td className="px-6 py-4 max-w-xs truncate" title={r.message}>{r.message}</td>
              <td className="px-6 py-4 text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ApplicationsTable({ rows }) {
  if (rows.length === 0) return <div className="bg-white border border-gray-200 p-12 text-center text-gray-500">No applications yet.</div>;
  return (
    <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="admin-applications-table">
      <table className="w-full text-sm">
        <thead className="bg-[#F9F9F7]">
          <tr className="text-left text-xs tracking-widest uppercase text-gray-500">
            <th className="px-6 py-4">Name</th><th className="px-6 py-4">Contact</th><th className="px-6 py-4">Household</th><th className="px-6 py-4">Skills</th><th className="px-6 py-4">Start</th><th className="px-6 py-4">Budget</th><th className="px-6 py-4">Received</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gray-100 hover:bg-[#F9F9F7]">
              <td className="px-6 py-4 font-medium text-[#0A1128]">{r.full_name}</td>
              <td className="px-6 py-4">
                <div><a href={`mailto:${r.email}`} className="text-[#688A4E] hover:underline">{r.email}</a></div>
                <div className="text-xs text-gray-500">{r.phone}</div>
              </td>
              <td className="px-6 py-4 text-xs">
                <div>{r.household_size || "—"}</div>
                <div className="text-gray-500">Kids: {r.children_count}{r.elderly_care && " · Elderly"}{r.special_needs && " · SN"}{r.pets && " · Pets"}</div>
              </td>
              <td className="px-6 py-4"><div className="flex flex-wrap gap-1 max-w-[200px]">{(r.required_skills || []).map((s) => <span key={s} className="text-[10px] border border-gray-300 px-1.5 py-0.5">{s}</span>)}</div></td>
              <td className="px-6 py-4">{r.preferred_start_date || "—"}</td>
              <td className="px-6 py-4">{r.budget_range || "—"}</td>
              <td className="px-6 py-4 text-xs text-gray-500">{new Date(r.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
